import React from 'react';
import { Edit, Send, Play, Pause, Copy, Trash2, Mail, AlertTriangle } from 'lucide-react';
import { EmailTimeline } from './EmailTimeline';

/**
 * Card component for displaying an email sequence
 */
export function EmailSequenceCard({ 
  sequence, 
  templates = [],
  onEdit,
  onToggle,
  onSendTest,
  onDuplicate,
  onDelete,
  showToast
}) {
  const getTriggerLabel = (trigger) => {
    const triggerLabels = {
      'welcome': 'When someone subscribes',
      'purchase': 'When someone buys something',
      'booking-confirmed': 'When someone books a session',
      'course-enrolled': 'When someone enrolls in a course',
      'membership-activated': 'When someone joins membership',
      'product-download': 'When someone downloads a product',
      'newsletter-signup': 'When someone signs up for newsletter',
      'abandoned-cart': 'When someone abandons cart',
      'manual': 'Send manually',
      'custom': 'Custom trigger'
    };
    return triggerLabels[trigger] || trigger;
  };

  const emailCount = sequence.emails?.length || 0;
  const isActive = sequence.status === 'active';
  
  // Check for orphaned emails
  const templateIds = new Set(templates.map(t => t.id));
  const orphanedEmails = (sequence.emails || []).filter(email => {
    if (email.orphaned) return true;
    return email.templateId && !templateIds.has(email.templateId);
  });
  const hasOrphaned = orphanedEmails.length > 0;

  return (
    <div className={`bg-white rounded-xl shadow-md border p-6 hover:shadow-lg transition-shadow ${
      hasOrphaned ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-800 truncate">
              {sequence.name || 'Untitled Sequence'}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Orphaned warning */}
              {hasOrphaned && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">
                  <AlertTriangle size={12} />
                  {orphanedEmails.length} orphaned
                </div>
              )}
              {/* Status indicator */}
              <div className={`w-2 h-2 rounded-full ${
                isActive ? 'bg-green-500' : 'bg-gray-400'
              }`} />
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {isActive ? 'Active' : 'Paused'}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-1">
            Sends when: <span className="font-medium text-gray-800">{getTriggerLabel(sequence.trigger)}</span>
          </p>
          <p className="text-xs text-gray-500">
            {emailCount} email{emailCount !== 1 ? 's' : ''} in sequence
          </p>
        </div>
      </div>

      {/* Timeline Preview */}
      {emailCount > 0 && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
          <EmailTimeline 
            emails={sequence.emails || []} 
            templates={templates}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          {/* Toggle Active/Paused */}
          <button
            onClick={() => onToggle(sequence.id, sequence.status)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              isActive
                ? 'bg-green-50 text-green-700 hover:bg-green-100'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
            }`}
            title={isActive ? 'Pause sequence' : 'Activate sequence'}
          >
            {isActive ? <Pause size={14} /> : <Play size={14} />}
            {isActive ? 'Pause' : 'Activate'}
          </button>

          {/* Send Test */}
          <button
            onClick={() => onSendTest(sequence)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition"
            title="Send test email"
          >
            <Send size={14} />
            Test
          </button>
        </div>

        <div className="flex items-center gap-1">
          {/* Edit */}
          <button
            onClick={() => onEdit(sequence)}
            className="p-2 text-gray-400 hover:text-[#c5a059] transition rounded-lg hover:bg-gray-50"
            title="Edit sequence"
          >
            <Edit size={18} />
          </button>

          {/* Duplicate */}
          <button
            onClick={() => onDuplicate(sequence)}
            className="p-2 text-gray-400 hover:text-blue-600 transition rounded-lg hover:bg-gray-50"
            title="Duplicate sequence"
          >
            <Copy size={18} />
          </button>

          {/* Delete */}
          <button
            onClick={() => {
              if (window.confirm(`Are you sure you want to delete "${sequence.name}"?`)) {
                onDelete(sequence.id);
              }
            }}
            className="p-2 text-gray-400 hover:text-red-600 transition rounded-lg hover:bg-gray-50"
            title="Delete sequence"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}



